import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserTypes } from "api";

export interface ChatContact2 {
    userId: number;
    fullName: string;
    profileImage: string;
    bgColor: string;
    foreColor: string;
    abbrName: string;
    isFavorite: boolean;
    lastChatText: string;
    lastChatTime: string;
    unreadCount?: number;
    unreadTagMe?: number;
    companyId?: number;
    isSaleCorrect?: boolean;
    isCsCorrect?: boolean;
}

export interface ChatContactState2 {
    chatContacts: ChatContact2[];
    currentPage: number;
    nextPage: number;
    // totalPages: number;
    // totalRecords: number;
    hasMore: boolean;
    loading: boolean;
    filter: string;
    errorMsg: string;
    // total: number;
    // loading: boolean;
    // error: string | null;
    // page: number;

    chatContactsSup: ChatContact2[];
    currentPageSup: number;
    nextPageSup: number;
    hasMoreSup: boolean;
    loadingSup: boolean;
    filterSup: string;
    errorMsgSup: string;

    // 20261020: Customer/Supplier unread over ALL rooms (server fn_getchatunreadtotals) - the lists are paged,
    // so summing loaded rooms missed unread rooms not loaded yet. null = not received yet (selector falls
    // back to the loaded-room sum). Set from chat-get-unread-totals / SignalR Chat_UnreadTotals, adjusted
    // locally by room-count changes in between.
    unreadTotalCust: number | null;
    unreadTotalSup: number | null;
}

// apply a room's unread change to the matching total (only once the server total is known)
const adjustUnreadTotal = (state: ChatContactState2, isSup: boolean, delta: number) => {
    if (!delta) return;
    if (isSup) {
        if (state.unreadTotalSup != null) state.unreadTotalSup = Math.max(0, state.unreadTotalSup + delta);
    } else {
        if (state.unreadTotalCust != null) state.unreadTotalCust = Math.max(0, state.unreadTotalCust + delta);
    }
}

const initialState: ChatContactState2 = {
    chatContacts: [],
    currentPage: 1,
    nextPage: 1,
    // totalPages: 0,
    // totalRecords: 0,
    hasMore: false,
    loading: false,
    filter: "",
    errorMsg: "",

    chatContactsSup: [],
    currentPageSup: 1,
    nextPageSup: 1,
    hasMoreSup: false,
    loadingSup: false,
    filterSup: "",
    errorMsgSup: "",

    unreadTotalCust: null,
    unreadTotalSup: null,
}
const chatContactSlice = createSlice({
    name: "chatContacts",
    initialState,
    reducers: {

        fetchChatContactsRequest(
            state,
            action: PayloadAction<{
                page: number; filter: string, isFetchNext: boolean,
                filterUnread?: boolean | null, filterPersonUnRead?: boolean | null, filterNoActivity?: boolean | null, filterSaleIds?: number[], filterCsIds?: number[], filterProvinceIds?: number[], groupType: UserTypes,
                currentSelectedChatId: any, companyType?: string
            }>
        ) {
            // console.log('fetchChatContactsRequest:', action.payload);
            if (action.payload.groupType == UserTypes.Group) {
                state.loading = true;
                state.errorMsg = "";
                state.filter = action.payload.filter;
            } else if (action.payload.groupType == UserTypes.GroupSupplier) {
                state.loadingSup = true;
                state.errorMsgSup = "";
                state.filterSup = action.payload.filter;
            }
            // state.error = null;
        },

        fetchChatContactsSuccess(
            state,
            action: PayloadAction<{ chatContacts: ChatContact2[]; currentPage: number; nextPage: number, hasMore: boolean, isFetchNext: boolean, groupType: UserTypes }>
        ) {
            const isFirstFetch = !action.payload.isFetchNext;
            // console.log('fetch chat contact success : ',{isFirstFetch});

            //state.chatContacts = [...state.chatContacts, ...action.payload.chatContacts]; //this code if save react code, data will duplicate

            // Extract existing userIds from state.chatContacts
            // const existingUserIds = new Set(state.chatContacts.map(contact => contact.userId)); // before 20261020: Supplier pages were de-duplicated against the Customer list
            const existingUserIds = new Set((action.payload.groupType == UserTypes.GroupSupplier ? state.chatContactsSup : state.chatContacts).map(contact => contact.userId));

            // Filter out duplicate entries from the payload
            const newContacts = isFirstFetch ? action.payload.chatContacts : action.payload.chatContacts.filter(contact => !existingUserIds.has(contact.userId));

            if (action.payload.groupType == UserTypes.Group) {
                // Append only unique contacts to the state
                state.chatContacts = isFirstFetch ? [...newContacts] : [...state.chatContacts, ...newContacts];

                // state.totalPages = action.payload.totalPages;
                state.currentPage = action.payload.currentPage;
                state.nextPage = action.payload.nextPage;
                state.hasMore = action.payload.hasMore;
                state.loading = false;
                state.errorMsg = "";
            } else if (action.payload.groupType == UserTypes.GroupSupplier) {
                state.chatContactsSup = isFirstFetch ? [...newContacts] : [...state.chatContactsSup, ...newContacts];
                state.currentPageSup = action.payload.currentPage;
                state.nextPageSup = action.payload.nextPage;
                state.hasMoreSup = action.payload.hasMore;
                state.loadingSup = false;
                state.errorMsgSup = "";
            }
        },
        fetchChatContactsFailure(state, action: PayloadAction<string>) {
            state.loading = false;
            state.errorMsg = action.payload;
        },
        swapChatRooms: (state, action: PayloadAction<{ index1: number; index2: number }>) => {
            const { index1, index2 } = action.payload;

            // Swap items using Immer's draft state
            const temp = state.chatContacts[index1];
            state.chatContacts[index1] = state.chatContacts[index2];
            state.chatContacts[index2] = temp;
        },
        // countNewUnread (20261020): the room is not loaded yet and a message from someone else arrived ->
        // that is one more unread for the tab total (the room's earlier unread is already in the server total)
        swapChatToTop: (state, action: PayloadAction<{ groupType: UserTypes, newMsg: ChatContact2, countNewUnread?: boolean }>) => {
            // console.log('swapChatToTop:', action.payload);
            const { groupType, newMsg, countNewUnread } = action.payload;

            if (groupType == UserTypes.Group) {
                // Find the index of the contact with the same userId
                const existingIndex = state.chatContacts.findIndex(
                    contact => contact.userId === newMsg.userId
                );
                // console.log('swap chat to top: ' + existingIndex);
                if (existingIndex !== 0) { // not already on top
                    if (existingIndex !== -1) {
                        // Move the existing contact to the top by swapping
                        const [existingContact] = state.chatContacts.splice(existingIndex, 1);
                        state.chatContacts.unshift(existingContact);
                    } else {
                        // If not found, insert the newMsg at the top
                        state.chatContacts.unshift(newMsg);
                        if (countNewUnread && (newMsg.unreadCount ?? 0) > 0) adjustUnreadTotal(state, false, 1);
                    }
                }
            } else if (groupType == UserTypes.GroupSupplier) {
                // Find the index of the contact with the same userId
                const existingIndex = state.chatContactsSup.findIndex(
                    contact => contact.userId === newMsg.userId
                );
                // console.log('swap chat to top: ' + existingIndex);
                if (existingIndex !== 0) { // not already on top
                    if (existingIndex !== -1) {
                        // Move the existing contact to the top by swapping
                        const [existingContact] = state.chatContactsSup.splice(existingIndex, 1);
                        state.chatContactsSup.unshift(existingContact);
                    } else {
                        // If not found, insert the newMsg at the top
                        state.chatContactsSup.unshift(newMsg);
                        if (countNewUnread && (newMsg.unreadCount ?? 0) > 0) adjustUnreadTotal(state, true, 1);
                    }
                }
            }
        },
        markReadAll: (state, action: PayloadAction<{ userOrGroupId: number }>) => {
            const { userOrGroupId } = action.payload;
            const chatContact = state.chatContacts.find(contact => contact.userId === userOrGroupId);
            if (chatContact) {
                adjustUnreadTotal(state, false, -(chatContact.unreadCount ?? 0)); // exact total follows via Chat_UnreadTotals
                chatContact.unreadCount = 0;
            }
            const chatContactSup = state.chatContactsSup.find(contact => contact.userId === userOrGroupId);
            if (chatContactSup) {
                adjustUnreadTotal(state, true, -(chatContactSup.unreadCount ?? 0));
                chatContactSup.unreadCount = 0;
            }
        },
        // 20261020: optimistic - the user just read `by` messages of this room (read request sent, server not answered yet).
        // Room + tab total drop now; UpdateMyUnRead / Chat_UnreadTotals from the server overwrite with exact numbers.
        decreaseUnreadCount: (state, action: PayloadAction<{ userId: number, by: number }>) => {
            const { userId, by } = action.payload;
            if (!by || by <= 0) return;
            const chatContact = state.chatContacts.find(contact => contact.userId === userId);
            if (chatContact) {
                const newCount = Math.max(0, (chatContact.unreadCount ?? 0) - by);
                adjustUnreadTotal(state, false, newCount - (chatContact.unreadCount ?? 0));
                chatContact.unreadCount = newCount;
                return;
            }
            const chatContactSup = state.chatContactsSup.find(contact => contact.userId === userId);
            if (chatContactSup) {
                const newCount = Math.max(0, (chatContactSup.unreadCount ?? 0) - by);
                adjustUnreadTotal(state, true, newCount - (chatContactSup.unreadCount ?? 0));
                chatContactSup.unreadCount = newCount;
            }
        },
        setChatUnreadTotals: (state, action: PayloadAction<{ custUnread: number, supUnread: number }>) => {
            state.unreadTotalCust = Number(action.payload.custUnread ?? 0);
            state.unreadTotalSup = Number(action.payload.supUnread ?? 0);
        },
        refreshChatUnreadTotals: (state) => { }, // saga: GET chat-get-unread-totals -> setChatUnreadTotals
        updateUnreadCount: (
            state,
            action: PayloadAction<{ userId: number, unreadCount: number, unreadTagMe: number, lastChatText: string, lastChatTime: string }>
        ) => {
            // console.log('xxx');
            const { userId, unreadCount, unreadTagMe, lastChatText, lastChatTime } = action.payload;

            // Find the chat contact by userId
            const chatContact = state.chatContacts.find(contact => contact.userId === userId);

            if (chatContact) {
                // Update the properties if the contact is found
                adjustUnreadTotal(state, false, (unreadCount ?? 0) - (chatContact.unreadCount ?? 0)); // 20261020
                chatContact.unreadCount = unreadCount;
                chatContact.unreadTagMe = unreadTagMe;
                if (lastChatText && lastChatText != "") chatContact.lastChatText = lastChatText;
                if (lastChatTime && lastChatTime != "") chatContact.lastChatTime = lastChatTime;
            } else { // 20250915 not found group chat in customer mode, then find in supplier mode
                const chatContactsSup = state.chatContactsSup.find(contact => contact.userId === userId);
                if (chatContactsSup) {
                    adjustUnreadTotal(state, true, (unreadCount ?? 0) - (chatContactsSup.unreadCount ?? 0)); // 20261020
                    chatContactsSup.unreadCount = unreadCount;
                    chatContactsSup.unreadTagMe = unreadTagMe;
                    if (lastChatText && lastChatText != "") chatContactsSup.lastChatText = lastChatText;
                    if (lastChatTime && lastChatTime != "") chatContactsSup.lastChatTime = lastChatTime;
                }
            }
        },

        // Use when user map chat room with customer id from dconnect
        setCustomerId: (
            state,
            action: PayloadAction<{ userOrGroupId: number, companyId: number | undefined }>
        ) => {
            const { userOrGroupId, companyId } = action.payload;

            // find group
            const chatContact = state.chatContacts.find(contact => contact.userId === userOrGroupId);
            if (chatContact) {
                chatContact.companyId = companyId;
            }
        },

        changeGroupFullName: (
            state,
            action: PayloadAction<{ groupId: any, fullName: any }>
        ) => {
            const { groupId, fullName } = action.payload;
            // find group
            const chatContact = state.chatContacts.find(contact => contact.userId === groupId);
            if (chatContact) {
                chatContact.fullName = fullName;
            }

        },


    },
});

export const {
    fetchChatContactsRequest,
    fetchChatContactsSuccess,
    fetchChatContactsFailure,
    swapChatRooms,
    markReadAll,
    updateUnreadCount,
    swapChatToTop,
    setCustomerId,
    changeGroupFullName,
    setChatUnreadTotals,
    refreshChatUnreadTotals,
    decreaseUnreadCount
} = chatContactSlice.actions;

export default chatContactSlice.reducer;